import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { faUser } from '@fortawesome/pro-regular-svg-icons/faUser';
import { Button, Form, Select, Space, Typography } from 'antd';
import { PosModal } from 'pos-client/components/shared/Display/Modal/PosModal.tsx';
import { sortStrings } from 'pos-client/utils/dataTableHelpers.tsx';

type AssignToLocationModalProps<T> = {
  title: string;
  selectOptions: T[];
  label: string;
  okText?: string;
  contentText?: string;
  loading: boolean;
  assigning: boolean;
  open: boolean;
  // eslint-disable-next-line
  handleAssign: (...args: any) => any;
  onClose?: () => void;
};

export const AssignToLocationModal = <
  T extends { label: string; value: string },
>({
  title,
  selectOptions,
  loading,
  assigning,
  open,
  label,
  okText = 'Ok',
  contentText,
  handleAssign,
  onClose,
}: AssignToLocationModalProps<T>) => {
  const [selected, setSelected] = useState<string[]>([]);
  const { t } = useTranslation();

  const onOk = () => {
    handleAssign(selected);
    setSelected([]);
  };

  return (
    <PosModal
      header={{ title, icon: faUser }}
      open={open}
      onCancel={() => {
        setSelected([]);
        onClose?.();
      }}
      footer={
        <Space>
          <Button
            onClick={() => {
              setSelected([]);
              onClose?.();
            }}
          >
            {t('common.buttons.cancel')}
          </Button>
          <Button
            type="primary"
            onClick={onOk}
            disabled={selected.length === 0}
            loading={loading || assigning}
          >
            {okText}
          </Button>
        </Space>
      }
    >
      <Space direction="vertical" size={20} style={{ width: '100%' }}>
        {contentText && <Typography.Text>{contentText}</Typography.Text>}

        <Form layout="vertical">
          <Form.Item rules={[{ required: true }]} label={label}>
            <Select
              value={selected}
              mode="multiple"
              onChange={(value) => setSelected(value)}
              options={selectOptions}
              optionFilterProp="label"
              filterSort={(optionA, optionB) =>
                sortStrings(optionA, optionB, 'label')
              }
              showSearch
            />
          </Form.Item>
        </Form>
      </Space>
    </PosModal>
  );
};
